import { useEffect, useMemo, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { fmtMY } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import {
  Plus, Sparkle, Receipt, CaretDown, CaretRight, PencilSimple, Trash, DownloadSimple, FilePdf,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const TAX_FORMS = [
  { key: "form_c",  label: "Form C",                   rule: "FYE + 8 months" },
  { key: "cp204",   label: "Form CP204",               rule: "1 month before FYE" },
  { key: "cp204a1", label: "Form CP204A (1st revise)", rule: "FYE + 6 months" },
  { key: "cp204a2", label: "Form CP204A (2nd revise)", rule: "FYE + 9 months" },
  { key: "cp204a3", label: "Form CP204A (3rd revise)", rule: "FYE + 11 months" },
];
const STATUSES = ["pending", "in_progress", "submitted", "overdue", "exempt"];

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso + (iso.length <= 10 ? "T00:00:00Z" : ""));
  if (isNaN(d)) return null;
  const now = new Date();
  const utcNow = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const utcDue = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return Math.round((utcDue - utcNow) / (1000 * 60 * 60 * 24));
}
function effStatus(rec, fk) {
  const s = rec[`${fk}_status`] || "pending";
  if (s === "submitted" || s === "exempt") return s;
  const due = rec[`${fk}_due_date`];
  if (due && new Date(due) < new Date(new Date().toDateString())) return "overdue";
  return s;
}

const emptyClient = { name: "", ssm_no: "", financial_year_end: "", notes: "",
                       first_year_of_assessment: "", cp204_exempt_first_two_years: false };
const emptyRecord = () => {
  const b = { financial_year: "", notes: "" };
  for (const f of TAX_FORMS) {
    b[`${f.key}_due_date`] = "";
    b[`${f.key}_status`] = "pending";
    b[`${f.key}_submitted_date`] = "";
  }
  return b;
};

export default function TaxCompliance() {
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [rows, setRows] = useState([]);
  const [records, setRecords] = useState({}); // tax_client_id -> [records]
  const [alerts, setAlerts] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const [clientOpen, setClientOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [clientForm, setClientForm] = useState(emptyClient);

  const [recOpen, setRecOpen] = useState(false);
  const [recForClient, setRecForClient] = useState(null);
  const [editingRec, setEditingRec] = useState(null);
  const [recForm, setRecForm] = useState(emptyRecord());

  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [genYear, setGenYear] = useState(String(new Date().getFullYear()));
  const [genYears, setGenYears] = useState("1");
  const [generating, setGenerating] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/tax/clients"),
      api.get("/tax/alerts?window=30"),
    ]).then(async ([r, a]) => {
      const cs = r.data || [];
      setRows(cs);
      setAlerts(a.data || []);
      const results = await Promise.all(cs.map(c =>
        api.get(`/tax/clients/${c.id}/records`).then(r => [c.id, r.data]).catch(() => [c.id, []])
      ));
      const map = {}; for (const [id, arr] of results) map[id] = arr;
      setRecords(map);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows
      .map(c => {
        const recs = (records[c.id] || []).slice().sort((a, b) =>
          String(b.financial_year).localeCompare(String(a.financial_year))
        );
        let soonest = null, overdueCount = 0, upcoming30 = 0;
        let within30Total = 0, within30Done = 0; // includes submitted + exempt
        for (const r of recs) {
          for (const f of TAX_FORMS) {
            const st = effStatus(r, f.key);
            const due = r[`${f.key}_due_date`];
            const days = daysUntil(due);
            // Track within-30 pane for the "all done?" banner
            if (days != null && days <= 30) {
              within30Total++;
              if (st === "submitted" || st === "exempt") within30Done++;
            }
            // Pending-only stats
            if (st === "submitted" || st === "exempt") continue;
            if (days == null) continue;
            if (days < 0) overdueCount++;
            else if (days <= 30) upcoming30++;
            if (!soonest || days < soonest.days) soonest = { due, kind: f.label, fy: r.financial_year, days };
          }
        }
        const allDoneWithin30 = within30Total > 0 && within30Done === within30Total;
        return { client: c, recs, soonest, overdueCount, upcoming30, allDoneWithin30 };
      })
      .filter(row => !s || (row.client.name || "").toLowerCase().includes(s) || (row.client.ssm_no || "").toLowerCase().includes(s))
      .sort((a, b) => (b.overdueCount - a.overdueCount) || ((a.soonest?.days ?? 1e9) - (b.soonest?.days ?? 1e9)));
  }, [rows, records, q]);

  const toggle = (id) => setExpanded(x => ({ ...x, [id]: !x[id] }));

  const startClient = (c) => {
    if (c) { setEditingClient(c); setClientForm({ ...emptyClient, ...c }); }
    else   { setEditingClient(null); setClientForm(emptyClient); }
    setClientOpen(true);
  };
  const saveClient = async () => {
    if (!clientForm.name.trim() || !clientForm.financial_year_end.trim())
      return toast.error("Company name and FYE are required");
    const payload = {
      ...clientForm,
      first_year_of_assessment: clientForm.first_year_of_assessment
        ? parseInt(clientForm.first_year_of_assessment, 10) : null,
      cp204_exempt_first_two_years: !!clientForm.cp204_exempt_first_two_years,
    };
    try {
      if (editingClient) await api.put(`/tax/clients/${editingClient.id}`, payload);
      else await api.post("/tax/clients", payload);
      toast.success("Saved"); setClientOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const deleteClient = async (c) => {
    if (!window.confirm(`Delete ${c.name} and all its tax records?`)) return;
    await api.delete(`/tax/clients/${c.id}`); load();
  };

  const startRec = (client, rec) => {
    setRecForClient(client);
    if (rec) { setEditingRec(rec); setRecForm({ ...emptyRecord(), ...rec }); }
    else     { setEditingRec(null); setRecForm(emptyRecord()); }
    setRecOpen(true);
  };
  const saveRec = async () => {
    if (!recForm.financial_year.trim()) return toast.error("Financial year required");
    try {
      if (editingRec) await api.put(`/tax/records/${editingRec.id}`, recForm);
      else await api.post(`/tax/clients/${recForClient.id}/records`, recForm);
      toast.success("Saved"); setRecOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const deleteRec = async (rec) => {
    if (!window.confirm(`Delete FY ${rec.financial_year}?`)) return;
    await api.delete(`/tax/records/${rec.id}`); load();
  };

  const runImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/tax/clients/import-from-clients");
      toast.success(`${data.created} imported · ${data.skipped} skipped`);
      setImportOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setImporting(false); }
  };

  const runBulkGen = async () => {
    const startYear = parseInt(genYear, 10);
    const count = Math.max(1, Math.min(10, parseInt(genYears, 10) || 1));
    if (!startYear) return toast.error("Enter a valid FY");
    setGenerating(true);
    let created = 0, skipped = 0, failed = 0;
    try {
      for (const c of rows) {
        const existing = records[c.id] || [];
        for (let i = 0; i < count; i++) {
          const fy = String(startYear + i);
          if (existing.some(x => String(x.financial_year) === fy)) { skipped++; continue; }
          try {
            const payload = { ...emptyRecord(), financial_year: fy, notes: "Auto-generated from FYE" };
            await api.post(`/tax/clients/${c.id}/records`, payload);
            created++;
          } catch { failed++; }
        }
      }
      toast.success(`${created} created · ${skipped} existed · ${failed} failed`);
      setGenOpen(false); load();
    } finally { setGenerating(false); }
  };

  return (
    <div className="p-10 max-w-[1400px]">
      <header className="flex items-end justify-between mb-8 gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">LHDN Malaysia · Standalone</div>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight">Tax Clients</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            Independent tax tracker — company name and FYE only. Not linked to Clients; edits here do not affect the company secretary records.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" className="rounded-sm h-10"
                  onClick={() => {
                    const token = localStorage.getItem("cosec_token");
                    window.open(`${API}/tax/clients/listing/pdf?auth=${token}`, "_blank");
                  }}
                  data-testid="tax-listing-pdf-btn">
            <FilePdf size={16} weight="duotone" className="mr-2" /> Tax Clients PDF
          </Button>
          <Button variant="outline" className="rounded-sm h-10" onClick={() => setImportOpen(true)} data-testid="tax-import-btn">
            <DownloadSimple size={16} weight="duotone" className="mr-2" /> Copy from Clients
          </Button>
          <Button variant="outline" className="rounded-sm h-10" onClick={() => setGenOpen(true)} data-testid="tax-bulk-gen-btn">
            <Sparkle size={16} weight="duotone" className="mr-2" /> Bulk auto-generate
          </Button>
          <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE] h-10" onClick={() => startClient(null)} data-testid="tax-add-client-btn">
            <Plus size={16} weight="bold" className="mr-2" /> New Tax Client
          </Button>
        </div>
      </header>

      <div className="mb-6" />
      {alerts.length > 0 && (
        <div className="mb-4 border-l-4 border-l-[#DA1E28] bg-[#DA1E28]/5 px-4 py-2 flex items-center gap-3"
             data-testid="tax-alerts-summary">
          <span className="inline-block h-2 w-2 rounded-full bg-[#DA1E28] animate-pulse" />
          <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#DA1E28]">
            {alerts.length} tax submission{alerts.length === 1 ? "" : "s"} within 30 days · surfaced at top of the table
          </span>
        </div>
      )}

      <div className="mb-4 flex items-center gap-3">
        <Input placeholder="Search company or SSM…" value={q} onChange={e => setQ(e.target.value)}
               className="max-w-md rounded-sm" data-testid="tax-search-input" />
        <span className="font-mono text-[11px] text-muted-foreground">{filtered.length} companies</span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-sm h-8"
                  onClick={() => setExpanded(Object.fromEntries(rows.map(c => [c.id, true])))}>
            Expand all
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm h-8" onClick={() => setExpanded({})}>
            Collapse all
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-border p-10 text-sm text-muted-foreground flex flex-col items-start gap-3">
          <Receipt size={22} weight="duotone" />
          No tax companies yet. Add one via <b>New Tax Client</b> or use <b>Copy from Clients</b> to snapshot from the secretary directory.
        </div>
      ) : (
        <div className="border border-border">
          {filtered.map(({ client, recs, soonest, overdueCount, upcoming30, allDoneWithin30 }, idx) => {
            const isOpen = !!expanded[client.id];
            // Rule:
            //   any within-30 deadline still pending  → RED
            //   all within-30 deadlines submitted/exempt  → GREEN
            //   nothing within 30 days               → neutral
            const banner =
              overdueCount > 0 || upcoming30 > 0 ? "border-l-4 border-l-[#DA1E28] bg-[#DA1E28]/[0.03]"
              : allDoneWithin30 ? "border-l-4 border-l-[#24A148] bg-[#24A148]/[0.05]"
              : "";
            return (
              <div key={client.id} className={`border-b last:border-b-0 border-border ${banner}`}>
                <div className="grid grid-cols-[24px_2fr_1fr_1.5fr_1fr_140px] items-center gap-3 px-6 py-3">
                  <button type="button" onClick={() => toggle(client.id)} data-testid={`tax-toggle-${idx}`}>
                    {isOpen ? <CaretDown size={14} weight="bold" /> : <CaretRight size={14} weight="bold" />}
                  </button>
                  <div className="min-w-0">
                    <div className="text-sm truncate">{client.name}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      FYE {client.financial_year_end || "—"}
                      {client.ssm_no ? ` · ${client.ssm_no}` : ""}
                      {isAdmin && (client.owner_name || client.owner_email) && (
                        <span className="ml-2 normal-case tracking-normal text-[10px] text-[#0F62FE]" data-testid={`tax-owner-${client.id}`}>
                          · {client.owner_name || client.owner_email}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-xs font-mono text-muted-foreground">{recs.length} FY record{recs.length === 1 ? "" : "s"}</div>
                  <div className="min-w-0">
                    {soonest ? (
                      <>
                        <div className="text-xs truncate">{soonest.kind} · FY {soonest.fy}</div>
                        <div className="font-mono text-[11px] text-muted-foreground">{fmtMY(soonest.due)}</div>
                      </>
                    ) : <span className="text-xs text-muted-foreground">No pending deadline</span>}
                  </div>
                  <div className="font-mono text-xs">
                    {soonest ? (
                      soonest.days < 0 ? <span className="text-[#DA1E28] font-medium">{Math.abs(soonest.days)} days overdue</span>
                      : soonest.days === 0 ? <span className="text-[#DA1E28] font-medium">Due today</span>
                      : <span className={soonest.days <= 30 ? "text-[#DA1E28] font-medium" : "text-foreground"}>{soonest.days} days left</span>
                    ) : allDoneWithin30 ? (
                      <span className="text-[#24A148] font-medium">All submitted</span>
                    ) : "—"}
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => startClient(client)} title="Edit">
                      <PencilSimple size={14} weight="bold" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-[#DA1E28]" onClick={() => deleteClient(client)} title="Delete">
                      <Trash size={14} weight="bold" />
                    </Button>
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-border bg-[#FAFAFA]">
                    {recs.length === 0 ? (
                      <div className="px-6 py-6 flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">No tax records yet.</span>
                        <Button size="sm" className="rounded-sm h-8 bg-[#0F62FE] hover:bg-[#0043CE]" onClick={() => startRec(client, null)}>
                          <Plus size={14} weight="bold" className="mr-1.5" /> Add FY
                        </Button>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[900px]">
                          <thead>
                            <tr className="border-t border-b border-border bg-white">
                              <th className="px-4 py-2 text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground w-[70px]">FY</th>
                              {TAX_FORMS.map(f => (
                                <th key={f.key} className="px-3 py-2 text-left text-xs font-medium" title={f.rule}>
                                  {f.label}
                                </th>
                              ))}
                              <th className="px-3 py-2 text-right w-[90px]">
                                <button type="button"
                                        onClick={() => startRec(client, null)}
                                        className="font-mono text-[10px] uppercase tracking-widest text-[#0F62FE] hover:underline inline-flex items-center gap-1"
                                        data-testid={`tax-add-fy-${client.id}`}>
                                  <Plus size={11} weight="bold" /> Add FY
                                </button>
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {recs.map(rec => (
                              <tr key={rec.id} className="data-row border-b last:border-b-0 border-border align-top">
                                <td className="px-4 py-3 font-mono text-sm">{rec.financial_year}</td>
                                {TAX_FORMS.map(f => {
                                  const due = rec[`${f.key}_due_date`];
                                  const eff = effStatus(rec, f.key);
                                  const bal = daysUntil(due);
                                  const sub = rec[`${f.key}_submitted_date`];
                                  const balColor =
                                    eff === "exempt" ? "text-muted-foreground"
                                    : eff === "submitted" ? "text-[#24A148]"
                                    : bal == null ? "text-muted-foreground"
                                    : bal <= 0 ? "text-[#DA1E28] font-medium"
                                    : bal <= 30 ? "text-[#F1C21B] font-medium"
                                    : "text-muted-foreground";
                                  const balText =
                                    eff === "exempt" ? "Exempt"
                                    : eff === "submitted" ? `Filed${sub ? " · " + fmtMY(sub) : ""}`
                                    : bal == null ? ""
                                    : bal < 0 ? `${Math.abs(bal)} days overdue`
                                    : bal === 0 ? "Due today"
                                    : `${bal} days left`;
                                  return (
                                    <td key={f.key} className="px-3 py-3">
                                      <div className="font-mono text-xs">{fmtMY(due) || "—"}</div>
                                      <div className={`font-mono text-[10px] uppercase tracking-widest ${balColor}`}>
                                        {balText}
                                      </div>
                                      <div className="mt-1.5">
                                        <InlineStatusSelect rec={rec} formKey={f.key} onSaved={load} compact />
                                      </div>
                                    </td>
                                  );
                                })}
                                <td className="px-3 py-3 text-right">
                                  <div className="flex items-center justify-end gap-0.5">
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => startRec(client, rec)} title="Edit FY">
                                      <PencilSimple size={13} weight="bold" />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-[#DA1E28]" onClick={() => deleteRec(rec)} title="Delete FY">
                                      <Trash size={13} weight="bold" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New / Edit Tax Client */}
      <Dialog open={clientOpen} onOpenChange={setClientOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader><DialogTitle>{editingClient ? "Edit" : "New"} Tax Client</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <F label="Company Name *">
              <Input value={clientForm.name} onChange={e => setClientForm({ ...clientForm, name: e.target.value })}
                     className="rounded-sm" data-testid="tax-client-name-input" />
            </F>
            <F label="SSM No. (optional)">
              <Input value={clientForm.ssm_no} onChange={e => setClientForm({ ...clientForm, ssm_no: e.target.value })}
                     className="rounded-sm" placeholder="e.g. 1234567-A" />
            </F>
            <F label="Financial Year End (MM-DD) *">
              <Input value={clientForm.financial_year_end} onChange={e => setClientForm({ ...clientForm, financial_year_end: e.target.value })}
                     className="rounded-sm" placeholder="12-31" data-testid="tax-client-fye-input" />
            </F>
            <F label="Notes">
              <Input value={clientForm.notes} onChange={e => setClientForm({ ...clientForm, notes: e.target.value })}
                     className="rounded-sm" />
            </F>
            <div className="border border-border p-4 space-y-3 bg-[#FAFAFA]">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                First-year CP204 exemption (LHDN, first 2 YAs)
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox"
                       checked={!!clientForm.cp204_exempt_first_two_years}
                       onChange={e => setClientForm({ ...clientForm, cp204_exempt_first_two_years: e.target.checked })}
                       data-testid="tax-client-cp204-exempt" />
                <span>Exempt CP204 submission for the first 2 Years of Assessment</span>
              </label>
              <F label="First Year of Assessment (YYYY)">
                <Input value={clientForm.first_year_of_assessment ?? ""}
                       onChange={e => setClientForm({ ...clientForm, first_year_of_assessment: e.target.value })}
                       className="rounded-sm" placeholder="e.g. 2026"
                       data-testid="tax-client-first-ya-input" />
              </F>
              <p className="text-[11px] text-muted-foreground">
                First YA = calendar year of the <b>first FYE after incorporation</b>. Example: incorporated 21/08/2026 with FYE 31/08 → first FYE = 31/08/2027, so First YA = <b>2027</b>. CP204 will then be EXEMPT for FY 2027 and FY 2028.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setClientOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" onClick={saveClient} data-testid="tax-client-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add / Edit FY record */}
      <Dialog open={recOpen} onOpenChange={setRecOpen}>
        <DialogContent className="max-w-3xl rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingRec ? "Edit" : "New"} tax record — {recForClient?.name} (FYE {recForClient?.financial_year_end})
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <F label="Financial Year *">
                <Input value={recForm.financial_year} onChange={e => setRecForm({ ...recForm, financial_year: e.target.value })}
                       className="rounded-sm" placeholder="2026" data-testid="tax-rec-fy-input" />
              </F>
            </div>
            {TAX_FORMS.map(f => (
              <div key={f.key} className="col-span-2 border border-border p-4">
                <div className="font-mono text-xs uppercase tracking-widest mb-3">
                  {f.label} <span className="text-muted-foreground normal-case">· {f.rule}</span>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <F label="Due Date (YYYY-MM-DD)">
                    <Input value={recForm[`${f.key}_due_date`]}
                           onChange={e => setRecForm({ ...recForm, [`${f.key}_due_date`]: e.target.value })}
                           placeholder="Auto-computed on save" className="rounded-sm" />
                  </F>
                  <F label="Status">
                    <Select value={recForm[`${f.key}_status`]}
                            onValueChange={v => setRecForm({ ...recForm, [`${f.key}_status`]: v })}>
                      <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </F>
                  <F label="Submitted Date">
                    <Input value={recForm[`${f.key}_submitted_date`]}
                           onChange={e => setRecForm({ ...recForm, [`${f.key}_submitted_date`]: e.target.value })}
                           placeholder="YYYY-MM-DD" className="rounded-sm" />
                  </F>
                </div>
              </div>
            ))}
            <div className="col-span-2">
              <F label="Notes">
                <Input value={recForm.notes} onChange={e => setRecForm({ ...recForm, notes: e.target.value })} className="rounded-sm" />
              </F>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setRecOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" onClick={saveRec} data-testid="tax-rec-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy from Clients */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader><DialogTitle>Copy from Clients</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Snapshots company name and FYE from your Clients directory. This creates a <b>separate</b> record here — no ongoing linkage. Duplicates (same name + FYE) are skipped.
          </p>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setImportOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" onClick={runImport} disabled={importing}
                    data-testid="tax-import-run-btn">
              {importing ? "Copying…" : "Copy now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk generate */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader><DialogTitle>Bulk auto-generate tax deadlines</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            For every tax company, create a record with the 5 LHDN form due dates. Existing FYs are skipped.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <F label="Starting FY">
              <Input value={genYear} onChange={e => setGenYear(e.target.value)} className="rounded-sm" placeholder="2026"
                     data-testid="tax-gen-fy-input" />
            </F>
            <F label="Number of Years">
              <Input type="number" min={1} max={10} value={genYears} onChange={e => setGenYears(e.target.value)}
                     className="rounded-sm" data-testid="tax-gen-count-input" />
            </F>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" onClick={runBulkGen} disabled={generating}
                    data-testid="tax-gen-run-btn">
              {generating ? "Generating…" : "Generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
const Th = ({ children }) =>
  <th className="px-6 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-normal">{children}</th>;

function InlineStatusSelect({ rec, formKey, onSaved, compact = false }) {
  const [saving, setSaving] = useState(false);
  const current = rec[`${formKey}_status`] || "pending";
  const change = async (v) => {
    if (v === current) return;
    setSaving(true);
    // Payload — spread the whole record and update just this form's status + submitted_date.
    // Setting to "submitted" auto-fills submitted_date if empty; clearing from submitted wipes it.
    const payload = { ...rec };
    delete payload.id; delete payload.tax_client_id; delete payload.created_at; delete payload.updated_at; delete payload._id;
    payload[`${formKey}_status`] = v;
    if (v === "submitted" && !payload[`${formKey}_submitted_date`]) {
      payload[`${formKey}_submitted_date`] = new Date().toISOString().slice(0, 10);
    } else if (v !== "submitted" && payload[`${formKey}_status`] !== "submitted") {
      // leave submitted_date alone if user simply flips to pending/in_progress
    }
    try {
      await api.put(`/tax/records/${rec.id}`, payload);
      toast.success(`Status → ${v.replace("_"," ")}`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Select value={current} onValueChange={change} disabled={saving}>
      <SelectTrigger className={compact
                        ? "rounded-sm h-6 w-full min-w-[110px] text-[10px] font-mono uppercase tracking-widest px-2"
                        : "rounded-sm h-7 w-[140px] text-[11px] font-mono uppercase tracking-widest"}
                     data-testid={`inline-status-${rec.id}-${formKey}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {STATUSES.map(s => <SelectItem key={s} value={s} className="text-xs font-mono uppercase tracking-widest">{s.replace("_", " ")}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
